<template>
  <div class="gi-root">

    <!-- Summary cards -->
    <!-- Kept on screen while a toggle change refetches, so the cards
         don't blink out; only hidden until the first load lands. -->
    <div class="gi-summary-cards" v-if="summaryLoaded" :class="{ 'gi-summary-cards--loading': loadingSummary }">
      <CurrencySummaryCard
        title="Last period"
        headline="Billed"
        :by-currency="lastPeriod.by_currency || {}"
        :metrics="PERIOD_METRICS"
      >
        <template #title-extra>
          <span v-if="billingType !== 'Both'" class="gi-card__scope">{{ billingType }}</span>
          <span v-if="filteredSummary" class="gi-card__scope gi-card__scope--filtered">Filtered</span>
        </template>
      </CurrencySummaryCard>

      <CurrencySummaryCard
        title="Current period"
        headline="Unbilled"
        :by-currency="currentPeriod.by_currency || {}"
        :metrics="PERIOD_METRICS"
        :variance="currentVariance"
      >
        <template #title-extra>
          <span v-if="billingType !== 'Both'" class="gi-card__scope">{{ billingType }}</span>
          <span v-if="filteredSummary" class="gi-card__scope gi-card__scope--filtered">Filtered</span>
          <span ref="currentPeriodInfoIcon" class="gi-info-icon"><b-icon icon="info-circle" /></span>
          <b-tooltip :target="() => $refs.currentPeriodInfoIcon" placement="top" triggers="hover focus">
            Approved + Draft
          </b-tooltip>
        </template>
      </CurrencySummaryCard>
    </div>

    <PendingInvoicesTable
      :key="'pending-' + refreshToken"
      :billing-type.sync="billingType"
      :billing-contacts-update="billingContactsUpdate"
      @changed="handleChanged"
      @filtered="handleDraftsFiltered"
      @billing-contacts-changed="handleBillingContactsChanged"
    />

    <ApprovedDispatchedInvoicesTable
      :key="'approved-' + refreshToken + '-' + (activeFilterMode || 'none')"
      :filter-mode="activeFilterMode"
      :billing-contacts-update="billingContactsUpdate"
      @changed="handleChanged"
      @billing-contacts-changed="handleBillingContactsChanged"
    />

  </div>
</template>

<script>
import { defineComponent, ref, computed, watch } from '@vue/composition-api'

import InvoicingService from '@services/InvoicingService'
import CurrencySummaryCard from './CurrencySummaryCard.vue'
import PendingInvoicesTable from './PendingInvoicesTable.vue'
import ApprovedDispatchedInvoicesTable from './ApprovedDispatchedInvoicesTable.vue'
import { pctVariance } from './draftSummary'

const PERIOD_METRICS = [
  { key: 'invoice_count', label: 'No. of invoices' },
  { key: 'bdx_count', label: 'No. of BDX' },
]

export default defineComponent({
  name: 'GenerateInvoicesTab',

  components: {
    CurrencySummaryCard,
    PendingInvoicesTable,
    ApprovedDispatchedInvoicesTable,
  },

  props: {
    // 'needs-attention' | 'changed-after-approve' | null — set via the
    // header warning icon / this page's "changed since approval" banner.
    filterMode: {
      type: String,
      default: null,
    },
  },

  setup(props) {
    const summary = ref({})
    const loadingSummary = ref(false)
    const summaryLoaded = ref(false)

    // The Draft table's Market / LIMOSS / Both toggle — both summary cards
    // are narrowed to it (see Lincoln's InvoiceSummaryView billing_type).
    const billingType = ref('Both')

    // Bumped on any Pending/Approved table 'changed' event — forces both
    // child tables to remount and refetch rather than optimistically
    // mutating local state, since Lincoln (not LimeIQ) owns invoice truth.
    const refreshToken = ref(0)

    // Route-driven filter mode ('needs-attention' | 'changed-after-approve')
    // passed straight through to the Approved table — set via the header
    // warning icon / this page's own banner.
    const activeFilterMode = ref(props.filterMode)
    watch(() => props.filterMode, (val) => { activeFilterMode.value = val })

    // Once the Draft table is searched or column-filtered (invoice no.,
    // TPA, underwriter, …), both cards show the figures for just the rows it
    // matches, which the drafts endpoint returns with each page (see
    // _draft_listing in LimeIQ's invoice proxy). Otherwise (null) they show
    // the server's figures, which already follow the Market / LIMOSS / Both
    // toggle.
    const filteredSummary = ref(null)
    const handleDraftsFiltered = ({ summary: draftSummary }) => { filteredSummary.value = draftSummary || null }
    const shownSummary = computed(() => filteredSummary.value || summary.value || {})

    const lastPeriod = computed(() => shownSummary.value.last_period || {})
    const currentPeriod = computed(() => shownSummary.value.current_period || {})
    const variance = computed(() => shownSummary.value.variance || {})

    // Variance of the current period against the last, for the currency
    // picked on the card: the server's (or filtered) figures for All, worked
    // out from the two cards' per-currency totals otherwise.
    const currentVariance = (currency) => {
      if (currency === 'All') return variance.value
      const now = (currentPeriod.value.by_currency || {})[currency] || {}
      const before = (lastPeriod.value.by_currency || {})[currency] || {}
      return {
        amount: pctVariance(now.amount || 0, before.amount || 0),
        invoice_count: pctVariance(now.invoice_count || 0, before.invoice_count || 0),
        bdx_count: pctVariance(now.bdx_count || 0, before.bdx_count || 0),
      }
    }

    const loadSummary = async () => {
      loadingSummary.value = true
      try {
        const requested = billingType.value
        const resp = await InvoicingService.getInvoiceSummary(
          requested === 'Both' ? {} : { billing_type: requested }
        )
        // A quicker toggle may have moved on while this was in flight.
        if (requested !== billingType.value) return
        const inner = resp.data && resp.data.data
        summary.value = inner && inner.data
        summaryLoaded.value = true
      } catch (e) {
        console.error('Failed to load invoice summary:', e)
      } finally {
        loadingSummary.value = false
      }
    }

    watch(billingType, loadSummary)

    const handleChanged = () => {
      // The Draft table remounts with no search/filters.
      filteredSummary.value = null
      refreshToken.value += 1
      loadSummary()
    }

    // A Billing Contacts popup change in either table (LIQ-2713) is handed to
    // both as a prop rather than remounting them, so the open popup stays up.
    // A fresh object every time, so the same change twice still fires.
    const billingContactsUpdate = ref(null)
    const handleBillingContactsChanged = (payload) => {
      billingContactsUpdate.value = { ...payload }
    }

    loadSummary()

    return {
      billingContactsUpdate,
      handleBillingContactsChanged,
      summary,
      loadingSummary,
      summaryLoaded,
      billingType,
      filteredSummary,
      handleDraftsFiltered,
      refreshToken,

      activeFilterMode,

      lastPeriod,
      currentPeriod,
      currentVariance,
      PERIOD_METRICS,

      handleChanged,
    }
  },
})
</script>

<style scoped>
.gi-root { padding: 0; }

.gi-root >>> .card .card-body {
    padding: 0 1.5rem!important;
}
.gi-root >>> .container {
    padding: 0!important;
    max-width: initial!important;
}
.gi-root >>> .table-responsive {
    max-height: 300px
}

.gi-summary-cards {
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin-bottom: 24px;
}
@media (max-width: 992px) {
  .gi-summary-cards { grid-template-columns: minmax(0, 1fr); }
}
.gi-card__scope {
  display: inline-block; margin-left: 6px; padding: 1px 8px; border-radius: 10px;
  background: #e8f1f2; color: #1c4a50; font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.04em; vertical-align: middle;
}
.gi-summary-cards--loading { opacity: 0.55; transition: opacity 0.15s ease; }
.gi-card__scope--filtered { background: #fff4e0; color: #b26a00; }

.gi-info-icon { cursor: help; opacity: 0.85; font-size: 12px; color: #8898aa; }

</style>
